// AuthContext.jsx
// Responsibility: User authentication, session state, and user profile data
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { profile as appProfile, fetchOrCreateApiKey, clearApiKey } from '../app-client';
import { API_KEY_STORAGE_KEY } from '../config';

const AuthContext = createContext();

export const AuthProvider = ({ children, user, signOut }) => {
    // --- Authentication State ---
    const [currentUser, setCurrentUser] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [isSessionValid, setIsSessionValid] = useState(true);
    const [isLoadingUser, setIsLoadingUser] = useState(true);

    // State for the detailed user profile needed by SettingsPage
    const [userProfile, setUserProfile] = useState(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [profileError, setProfileError] = useState(null);

    // Track if refresh is in progress
    const isRefreshingRef = useRef(false);

    // Derived user information
    const userId = currentUser?.userId || currentUser?.sub;
    const userEmail = currentUser?.attributes?.email || currentUser?.signInDetails?.loginId;
    const userName = currentUser?.attributes?.name || userEmail?.split('@')[0];

    // --- Authentication Session Management ---
    useEffect(() => {
        console.log('AuthContext: Setting up session validity checker');
        
        const checkSession = async () => {
            try {
                const session = await fetchAuthSession();
                
                if (!session?.tokens?.idToken) {
                    setIsSessionValid(false);
                    setAuthError('Your session has expired. Please sign in again.');
                    console.log('AuthContext: Session expired');
                } else {
                    setIsSessionValid(true);
                    setAuthError(null);
                }
            } catch (error) {
                console.error('AuthContext: Session check failed:', error);
                setIsSessionValid(false);
                setAuthError('Authentication error. Please sign in again.');
            }
        };

        // Initial check
        checkSession();

        // Check session every 5 minutes
        const interval = setInterval(checkSession, 5 * 60 * 1000);

        return () => {
            console.log('AuthContext: Cleaning up session checker');
            clearInterval(interval);
        };
    }, []);

    // Function to fetch and normalize the detailed user profile
    const fetchAndSetUserProfile = useCallback(async () => {
        if (!userId) return; // Don't fetch if there's no user

        // Prevent concurrent refreshes
        if (isRefreshingRef.current) {
            console.log('AuthContext: Profile refresh already in progress, skipping');
            return;
        }

        console.log('AuthContext: Fetching detailed user profile...');
        isRefreshingRef.current = true; 
        setIsLoadingProfile(true);
        setProfileError(null);

        try {
            const apiData = await appProfile.get();

            // First-ever load returns the raw aridel_live_* key once — seed it
            // into sessionStorage so aridel-client can use it for public-API calls.
            if (apiData.newApiKey && !sessionStorage.getItem(API_KEY_STORAGE_KEY)) {
                sessionStorage.setItem(API_KEY_STORAGE_KEY, apiData.newApiKey);
            }

            // Normalize the API data to match the structure expected by SettingsPage.jsx
            const normalizedProfile = {
                subscriptionTier: apiData.user.subscriptionTier || 'free',
                planTier: apiData.user.planTier || 'free',
                usageCredits: apiData.credits.remaining,
                creditsPerDollar: apiData.credits.creditsPerDollar || 1000,
                useOwnKey: apiData.preferences.useOwnKey,
                apiKeys: apiData.apiKeys,
            };

            setUserProfile(normalizedProfile);
            console.log('AuthContext: Detailed profile loaded and normalized:', normalizedProfile);
        } catch (error) {
            console.error('AuthContext: Failed to fetch detailed user profile:', error);
            setProfileError(error.message || 'Could not load profile data.');
        } finally {
            setIsLoadingProfile(false);
            isRefreshingRef.current = false;
        }
    }, [userId]);

    // --- Fetch Full User Attributes ---
    useEffect(() => {
        const loadUserProfile = async () => {
            if (!user) {
                console.log('AuthContext: No user provided, skipping profile load');
                setIsLoadingUser(false);
                setIsLoadingProfile(false);
                return;
            }

            try {
                setIsLoadingUser(true);

                let attributes;
                try {
                    // Fetch complete user attributes from Cognito
                    attributes = await fetchUserAttributes();
                    console.log('AuthContext: Fetched user attributes:', attributes);
                } catch (attrError) {
                    // Google/OAuth users: the Access Token lacks the
                    // aws.cognito.signin.user.attributes scope required by
                    // GetUser. Fall back to the ID token payload instead.
                    // Catch all errors (not just NotAuthorizedException) to guard
                    // against Amplify wrapping errors differently across versions.
                    console.log('AuthContext: fetchUserAttributes failed, reading from ID token payload', attrError.name);
                    const session = await fetchAuthSession();
                    const p = session.tokens?.idToken?.payload || {};
                    attributes = {
                        name: p.name ||
                              [p.given_name, p.family_name].filter(Boolean).join(' ') ||
                              undefined,
                        email: p.email,
                        email_verified: String(p.email_verified ?? false),
                        sub: p.sub,
                    };
                }

                // Combine the initial user object with the fetched attributes
                const completeUser = { ...user, attributes };
                setCurrentUser(completeUser);

            } catch (error) {
                console.error('AuthContext: Error fetching user attributes:', error);

                // Fallback to the initial user object if fetching fails
                setCurrentUser(user);

                // Set auth error if it's an authentication issue
                if (error.name === 'NotAuthorizedException' ||
                    error.name === 'UserNotFoundException') {
                    setAuthError('Failed to load user profile. Please sign in again.');
                    setIsSessionValid(false);
                }
            } finally {
                setIsLoadingUser(false);
            }
        };

        loadUserProfile();
    }, [user]); // This effect runs whenever the user object changes (i.e., on login)

    // A new effect to trigger profile fetch once the user is loaded
    useEffect(() => {
        if (currentUser) {
            fetchAndSetUserProfile();
        }
    }, [currentUser, fetchAndSetUserProfile]);

    // Ensure the user has an aridel_live_* key and seed it into sessionStorage
    // for public-API calls. Runs once per sign-in. If the user already has a
    // key (raw value unrecoverable), aridel-client falls back to the Cognito
    // JWT, so chat still works.
    useEffect(() => {
        if (!currentUser) return;
        if (sessionStorage.getItem(API_KEY_STORAGE_KEY)) return;
        fetchOrCreateApiKey().catch(err => {
            console.error('AuthContext: API key handshake failed:', err);
        });
    }, [currentUser]);

    // --- Handle Sign Out ---
    const handleSignOut = async () => {
        console.log(`AuthContext: User ${userId} signing out`);
        
        try {
            // Clear authentication state
            setCurrentUser(null);
            setAuthError(null);
            setIsSessionValid(false);
            clearApiKey();

            // Call the signOut function from Amplify
            if (signOut && typeof signOut === 'function') {
                await signOut();
                console.log('AuthContext: Sign out successful');
            } else {
                console.warn('AuthContext: signOut function not provided');
            }
        } catch (error) {
            console.error('AuthContext: Error during sign out:', error);
            // Even if sign out fails, clear local state
            setCurrentUser(null);
            setAuthError('Sign out failed, but session cleared locally.');
        }
    };

    // --- Utility Functions ---
    const clearAuthError = () => {
        setAuthError(null);
    };

    const refreshSession = async () => {
        console.log('AuthContext: Manually refreshing session');
        
        try {
            const session = await fetchAuthSession({ forceRefresh: true });
            
            if (session?.tokens?.idToken) {
                setIsSessionValid(true);
                setAuthError(null);
                console.log('AuthContext: Session refreshed successfully');
                return true;
            } else {
                setIsSessionValid(false);
                setAuthError('Failed to refresh session. Please sign in again.');
                return false;
            }
        } catch (error) {
            console.error('AuthContext: Failed to refresh session:', error);
            setIsSessionValid(false);
            setAuthError('Failed to refresh session. Please sign in again.');
            return false;
        }
    };

    const value = {
        // User data
        currentUser,
        userId,
        userEmail,
        userName,
        
        // Authentication state
        authError,
        isSessionValid,
        isLoadingUser,
        
        // Actions
        handleSignOut,
        clearAuthError,
        refreshSession,
        
        // Setters for other contexts to update auth state
        setAuthError,
        setIsSessionValid,

        // Profile data and actions for SettingsPage
        userProfile,
        isLoadingProfile,
        profileError,
        refreshUserProfile: fetchAndSetUserProfile, // Expose the fetch function as refresh
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};